import { createFileRoute, useBlocker } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { LucideIcon } from "lucide-react";
import {
  AlertCircle,
  Bell,
  Building2,
  ChartColumn,
  CheckCircle2,
  CircleDashed,
  CircleX,
  KeyRound,
  Loader2,
  Mail,
  Plug,
  RefreshCw,
  Search,
  Send,
  Eye,
  TriangleAlert,
} from "lucide-react";
import { toast } from "sonner";
import { useServerFn } from "@/lib/server-fn-compat";
import { useAuth } from "@/lib/auth-context";
import { ApiError } from "@/lib/api";
import { EMAIL_ERROR, isValidEmail, isValidPhone, PHONE_ERROR } from "@/lib/validate";
import {
  integrationStatusQueryOptions,
  INTEGRATION_STATUS_QUERY_KEY,
  settingsQueryOptions,
  SETTINGS_QUERY_KEY,
} from "@/lib/settings.queries";
import {
  sendTestAlert,
  setShopifyAdminToken,
  setTurnstileSecret,
  testIntegration,
  updateSettings,
  type HealthAlertMode,
  type IntegrationStatus,
  type IntegrationTestKind,
  type ProbeResult,
  type SettingsGroup,
  type SettingsResponse,
  type SettingsValues,
} from "@/lib/settings.functions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MediaField } from "@/components/admin/MediaField";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/admin/settings")({
  component: SettingsPage,
});

/* ------------------------------------------------------------------ draft */

/**
 * The form keeps list fields (recipients, address lines) as raw "one per
 * line" text so typing is never fought by a re-split; `fromDraft` turns the
 * draft back into the API shape at save and dirty-check time.
 */
type Draft = {
  org: Omit<SettingsValues["org"], "addressLines"> & { addressLines: string };
  notifications: {
    contactRecipients: string;
    researchRecipients: string;
    incubatorRecipients: string;
    newsletterSignupRecipients: string;
    healthAlertRecipients: string;
    healthAlertMode: HealthAlertMode;
  };
  email: SettingsValues["email"];
  seo: SettingsValues["seo"];
  integrations: SettingsValues["integrations"];
  analytics: SettingsValues["analytics"];
};

const joinLines = (xs: string[]) => xs.join("\n");
const splitLines = (s: string) => s.split(/\r?\n/).map((x) => x.trim()).filter(Boolean);

function toDraft(v: SettingsValues): Draft {
  return {
    org: { ...v.org, addressLines: joinLines(v.org.addressLines) },
    notifications: {
      contactRecipients: joinLines(v.notifications.contactRecipients),
      researchRecipients: joinLines(v.notifications.researchRecipients),
      incubatorRecipients: joinLines(v.notifications.incubatorRecipients),
      newsletterSignupRecipients: joinLines(v.notifications.newsletterSignupRecipients),
      healthAlertRecipients: joinLines(v.notifications.healthAlertRecipients),
      healthAlertMode: v.notifications.healthAlertMode,
    },
    email: { ...v.email },
    seo: { ...v.seo, social: { ...v.seo.social } },
    integrations: {
      ...v.integrations,
      turnstileEnabledForms: { ...v.integrations.turnstileEnabledForms },
    },
    analytics: { ...v.analytics },
  };
}

function fromDraft(d: Draft): SettingsValues {
  const t = (s: string) => s.trim();
  return {
    org: {
      companyName: t(d.org.companyName),
      legalName: t(d.org.legalName),
      addressLines: splitLines(d.org.addressLines),
      phone: t(d.org.phone),
      generalEmail: t(d.org.generalEmail),
      researchEmail: t(d.org.researchEmail),
      sponsorshipEmail: t(d.org.sponsorshipEmail),
      incubatorEmail: t(d.org.incubatorEmail),
    },
    notifications: {
      contactRecipients: splitLines(d.notifications.contactRecipients),
      researchRecipients: splitLines(d.notifications.researchRecipients),
      incubatorRecipients: splitLines(d.notifications.incubatorRecipients),
      newsletterSignupRecipients: splitLines(d.notifications.newsletterSignupRecipients),
      healthAlertRecipients: splitLines(d.notifications.healthAlertRecipients),
      healthAlertMode: d.notifications.healthAlertMode,
    },
    email: {
      senderName: t(d.email.senderName),
      senderAddress: t(d.email.senderAddress),
      replyTo: t(d.email.replyTo),
      subscribeSubject: t(d.email.subscribeSubject),
      newsletterFooter: d.email.newsletterFooter.trim(),
    },
    seo: {
      titleSuffix: t(d.seo.titleSuffix),
      defaultDescription: t(d.seo.defaultDescription),
      defaultShareImage: t(d.seo.defaultShareImage),
      social: {
        linkedin: t(d.seo.social.linkedin),
        x: t(d.seo.social.x),
        facebook: t(d.seo.social.facebook),
        instagram: t(d.seo.social.instagram),
        youtube: t(d.seo.social.youtube),
      },
      searchConsoleTag: t(d.seo.searchConsoleTag),
    },
    integrations: {
      turnstileSiteKey: t(d.integrations.turnstileSiteKey),
      turnstileEnabledForms: {
        contact: !!d.integrations.turnstileEnabledForms.contact,
        newsletter: !!d.integrations.turnstileEnabledForms.newsletter,
      },
    },
    analytics: {
      ga4MeasurementId: t(d.analytics.ga4MeasurementId),
      plausibleDomain: t(d.analytics.plausibleDomain),
      consentBannerEnabled: !!d.analytics.consentBannerEnabled,
      consentText: d.analytics.consentText.trim(),
      excludeAdmin: !!d.analytics.excludeAdmin,
    },
  };
}

/* ------------------------------------------------------------- validation */

type FieldErrors = Record<string, string>;

const URL_ERROR = "Enter a full web address starting with https://.";
const GA4_ID_RE = /^G-[A-Z0-9]{4,}$/;
const HOSTNAME_RE = /^(?!-)[a-z0-9-]+(\.[a-z0-9-]+)+$/i;
const TURNSTILE_SITE_KEY_RE = /^0x[A-Za-z0-9_-]{10,}$/;
const CONSENT_TEXT_MAX = 400;

function isValidUrl(v: string): boolean {
  if (!v) return true;
  try {
    const u = new URL(v);
    return u.protocol === "https:" || u.protocol === "http:";
  } catch {
    return false;
  }
}

/** Per-line email check for a recipients / list field. Empty lists are allowed here;
    the backend decides whether a recipient list may be empty. */
function emailLinesError(text: string): string | null {
  const bad = splitLines(text).filter((e) => !isValidEmail(e));
  if (bad.length === 0) return null;
  return bad.length === 1
    ? `"${bad[0]}" is not a valid email address.`
    : `${bad.length} lines are not valid email addresses: ${bad.slice(0, 3).join(", ")}${bad.length > 3 ? ", ..." : ""}`;
}

/** Client-side checks that mirror the obvious backend rules, so a typo never costs a round trip. */
function validateGroup(group: SettingsGroup, d: Draft): FieldErrors {
  const errs: FieldErrors = {};
  const email = (path: string, v: string, required: boolean) => {
    const t = v.trim();
    if (!t) {
      if (required) errs[path] = "Enter an email address.";
      return;
    }
    if (!isValidEmail(t)) errs[path] = EMAIL_ERROR;
  };
  if (group === "org") {
    if (!d.org.companyName.trim()) errs.companyName = "Enter the company name.";
    if (!isValidPhone(d.org.phone, false)) errs.phone = PHONE_ERROR;
    email("generalEmail", d.org.generalEmail, true);
    email("researchEmail", d.org.researchEmail, true);
    email("sponsorshipEmail", d.org.sponsorshipEmail, true);
    email("incubatorEmail", d.org.incubatorEmail, true);
  }
  if (group === "notifications") {
    const n = d.notifications;
    for (const key of [
      "contactRecipients",
      "researchRecipients",
      "incubatorRecipients",
      "newsletterSignupRecipients",
      "healthAlertRecipients",
    ] as const) {
      const e = emailLinesError(n[key]);
      if (e) errs[key] = e;
    }
  }
  if (group === "email") {
    if (!d.email.senderName.trim()) errs.senderName = "Enter a sender name.";
    email("senderAddress", d.email.senderAddress, true);
    email("replyTo", d.email.replyTo, false);
    if (!d.email.subscribeSubject.trim()) errs.subscribeSubject = "Enter a subject line.";
  }
  if (group === "seo") {
    if (!isValidUrl(d.seo.defaultShareImage) && !d.seo.defaultShareImage.startsWith("/"))
      errs.defaultShareImage = "Enter a full https:// address or a /media/... path.";
    for (const key of ["linkedin", "x", "facebook", "instagram", "youtube"] as const) {
      if (!isValidUrl(d.seo.social[key])) errs[`social.${key}`] = URL_ERROR;
    }
  }
  if (group === "integrations") {
    const key = d.integrations.turnstileSiteKey.trim();
    if (key && !TURNSTILE_SITE_KEY_RE.test(key))
      errs.turnstileSiteKey = "Enter the Turnstile site key (it starts with 0x).";
  }
  if (group === "analytics") {
    const id = d.analytics.ga4MeasurementId.trim();
    if (id && !GA4_ID_RE.test(id))
      errs.ga4MeasurementId = "Enter a GA4 measurement ID like G-XXXXXXXXXX (capital letters).";
    const domain = d.analytics.plausibleDomain.trim();
    if (domain && !HOSTNAME_RE.test(domain))
      errs.plausibleDomain = "Enter just the domain, e.g. www.miinews.com, without https://.";
    const consent = d.analytics.consentText.trim();
    if (d.analytics.consentBannerEnabled && !consent)
      errs.consentText = "Enter the text the consent banner shows.";
    else if (consent.length > CONSENT_TEXT_MAX)
      errs.consentText = `Keep the consent text under ${CONSENT_TEXT_MAX} characters.`;
  }
  return errs;
}

/* ------------------------------------------------------------------- page */

const GROUPS: SettingsGroup[] = [
  "org",
  "notifications",
  "email",
  "seo",
  "integrations",
  "analytics",
];

const GROUP_META: Record<
  SettingsGroup,
  { title: string; saveLabel: string; description: string; icon: LucideIcon }
> = {
  org: {
    title: "Organization and contact",
    saveLabel: "Save organization",
    description: "Company details and the addresses the public site links to.",
    icon: Building2,
  },
  notifications: {
    title: "Notifications",
    saveLabel: "Save notifications",
    description: "Who is emailed when a form is submitted or a health check fails.",
    icon: Bell,
  },
  email: {
    title: "Email sending and newsletter",
    saveLabel: "Save email settings",
    description: "The From identity for outbound mail and the newsletter defaults.",
    icon: Mail,
  },
  seo: {
    title: "SEO and social",
    saveLabel: "Save SEO",
    description: "Page titles, the default description and share image, social profiles.",
    icon: Search,
  },
  integrations: {
    title: "Integrations",
    saveLabel: "Save integrations",
    description:
      "The Shopify store connection and Cloudflare Turnstile spam protection for the public forms.",
    icon: Plug,
  },
  analytics: {
    title: "Analytics",
    saveLabel: "Save analytics",
    description:
      "Google Analytics 4 and Plausible, the consent banner, and whether admin pages are counted.",
    icon: ChartColumn,
  },
};

type PillTone = "ok" | "warn" | "fail" | "neutral";
type Pill = { tone: PillTone; label: string };

const PILL_STYLE: Record<PillTone, { icon: LucideIcon; className: string }> = {
  ok: { icon: CheckCircle2, className: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" },
  warn: { icon: TriangleAlert, className: "bg-amber-100 text-amber-900 hover:bg-amber-100" },
  fail: { icon: CircleX, className: "bg-red-100 text-red-800 hover:bg-red-100" },
  neutral: { icon: CircleDashed, className: "bg-muted text-muted-foreground hover:bg-muted" },
};

const PENDING_PILL: Pill = { tone: "neutral", label: "Checking..." };
const UNAVAILABLE_PILL: Pill = { tone: "neutral", label: "Status unavailable" };

/** The most recent probe wins: an inline Test result over the last status fetch. */
type Probes = Partial<Record<IntegrationTestKind, ProbeResult>>;

function storefrontPill(
  status: IntegrationStatus | undefined,
  probe: ProbeResult | undefined,
  failed: boolean,
): Pill {
  if (!status) return failed ? UNAVAILABLE_PILL : PENDING_PILL;
  if (!status.shopify.storeDomain) return { tone: "neutral", label: "Not configured" };
  const ok = probe ? probe.ok : status.shopify.storefront.ok;
  return ok ? { tone: "ok", label: "Connected" } : { tone: "fail", label: "Failing" };
}

function shopifyAdminPill(
  status: IntegrationStatus | undefined,
  probe: ProbeResult | undefined,
  failed: boolean,
): Pill {
  if (!status) return failed ? UNAVAILABLE_PILL : PENDING_PILL;
  if (!status.shopify.admin.configured && !probe)
    return { tone: "neutral", label: "Not configured" };
  const ok = probe ? probe.ok : status.shopify.admin.ok;
  if (ok === true) return { tone: "ok", label: "Connected" };
  if (ok === false) return { tone: "fail", label: "Failing" };
  return { tone: "warn", label: "Not tested" };
}

function turnstilePill(
  status: IntegrationStatus | undefined,
  probe: ProbeResult | undefined,
  failed: boolean,
): Pill {
  if (!status) return failed ? UNAVAILABLE_PILL : PENDING_PILL;
  if (!status.turnstile.secretConfigured) return { tone: "neutral", label: "No secret yet" };
  if (probe)
    return probe.ok ? { tone: "ok", label: "Verified" } : { tone: "fail", label: "Failing" };
  return { tone: "warn", label: "Not tested" };
}

const SHARE_IMAGE_TARGET = { bucket: "cms-images", prefix: "site", kind: "image" } as const;

function formatSaved(iso: string | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function SettingsPage() {
  const { isAdmin } = useAuth();
  const readOnly = !isAdmin;
  const qc = useQueryClient();
  const query = useQuery(settingsQueryOptions());
  const save = useServerFn(updateSettings);
  const sendTest = useServerFn(sendTestAlert);
  const saveToken = useServerFn(setShopifyAdminToken);
  const saveSecret = useServerFn(setTurnstileSecret);
  const runProbe = useServerFn(testIntegration);

  const [saved, setSaved] = useState<SettingsResponse | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [errors, setErrors] = useState<Partial<Record<SettingsGroup, FieldErrors>>>({});
  const [saving, setSaving] = useState<SettingsGroup | null>(null);

  // Seed the form once. Later refetches must not clobber a draft in progress;
  // saves update `saved` and the draft group themselves.
  useEffect(() => {
    if (query.data && !saved) {
      setSaved(query.data);
      setDraft(toDraft(query.data));
    }
  }, [query.data, saved]);

  const values = useMemo(() => (draft ? fromDraft(draft) : null), [draft]);

  const dirty = useMemo(() => {
    const out = {} as Record<SettingsGroup, boolean>;
    for (const g of GROUPS) {
      out[g] = !!values && !!saved && JSON.stringify(values[g]) !== JSON.stringify(saved[g]);
    }
    return out;
  }, [values, saved]);
  const dirtyGroups = GROUPS.filter((g) => dirty[g]);
  const anyDirty = dirtyGroups.length > 0;

  // Leaving with unsaved edits asks first (router navigation and tab close alike).
  const blocker = useBlocker({
    shouldBlockFn: () => anyDirty && !readOnly,
    enableBeforeUnload: () => anyDirty && !readOnly,
    withResolver: true,
  });

  const patch = useCallback(
    <G extends SettingsGroup>(group: G, partial: Partial<Draft[G]>) => {
      setDraft((d) => (d ? { ...d, [group]: { ...d[group], ...partial } } : d));
      setErrors((e) => (e[group] ? { ...e, [group]: undefined } : e));
    },
    [],
  );

  const saveGroup = useCallback(
    async (group: SettingsGroup): Promise<boolean> => {
      if (!draft || !values) return false;
      const clientErrors = validateGroup(group, draft);
      if (Object.keys(clientErrors).length > 0) {
        setErrors((e) => ({ ...e, [group]: clientErrors }));
        return false;
      }
      setSaving(group);
      try {
        const res = await save({ group, value: values[group] });
        setSaved(res);
        setDraft((d) => (d ? { ...d, [group]: toDraft(res)[group] } : d));
        setErrors((e) => ({ ...e, [group]: undefined }));
        qc.setQueryData(SETTINGS_QUERY_KEY, res);
        qc.invalidateQueries({ queryKey: ["public_settings"] });
        toast.success(`${GROUP_META[group].title} saved`);
        return true;
      } catch (e) {
        if (e instanceof ApiError && e.code === "VALIDATION" && e.fields) {
          setErrors((prev) => ({ ...prev, [group]: e.fields }));
          toast.error("Some fields need attention.");
        } else {
          toast.error(e instanceof Error ? e.message : "Save failed");
        }
        return false;
      } finally {
        setSaving(null);
      }
    },
    [draft, values, save, qc],
  );

  const saveAll = useCallback(async () => {
    for (const g of dirtyGroups) {
      const ok = await saveGroup(g);
      if (!ok) return;
    }
  }, [dirtyGroups, saveGroup]);

  const discardAll = useCallback(() => {
    if (saved) setDraft(toDraft(saved));
    setErrors({});
  }, [saved]);

  const testMut = useMutation({
    mutationFn: () => sendTest({ kind: "contact" }),
    onSuccess: (r) =>
      toast.success(
        r.to.length ? `Test email sent to ${r.to.join(", ")}` : "Test email sent",
      ),
    onError: (e) => toast.error(e instanceof Error ? e.message : "Could not send the test email"),
  });

  /* ---- integrations: live status, write-only secrets and probes.
     The secrets are deliberately NOT part of the draft: they are never read
     back, so they cannot take part in dirty tracking or group saves. ---- */
  const statusQuery = useQuery(integrationStatusQueryOptions());
  const status = statusQuery.data;
  const [adminToken, setAdminToken] = useState("");
  const [turnstileSecret, setTurnstileSecretValue] = useState("");
  const [probes, setProbes] = useState<Probes>({});
  const refreshStatus = useCallback(
    () => qc.invalidateQueries({ queryKey: INTEGRATION_STATUS_QUERY_KEY }),
    [qc],
  );

  const submitAdminToken = () => {
    const token = adminToken.trim();
    if (!/^shpat_/.test(token)) {
      toast.error("That does not look like an Admin API token; it should start with shpat_.");
      return;
    }
    tokenMut.mutate(token);
  };
  const submitTurnstileSecret = () => {
    const secret = turnstileSecret.trim();
    if (secret.length < 20) {
      toast.error("That does not look like a Turnstile secret key.");
      return;
    }
    secretMut.mutate(secret);
  };

  const tokenMut = useMutation({
    mutationFn: (token: string) => saveToken({ token }),
    onSuccess: (r) => {
      setAdminToken("");
      setProbes((p) => ({
        ...p,
        "shopify-admin": { ok: r.probe.ok, detail: r.probe.detail, checkedAt: r.lastChangedAt },
      }));
      void refreshStatus();
      // The Research product screen gates on this flag too.
      void qc.invalidateQueries({ queryKey: ["shopify-admin-status"] });
      if (r.probe.ok) toast.success("Admin token saved and verified");
      else toast.warning(`Token saved, but the check failed: ${r.probe.detail || "no detail"}`);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Could not save the token"),
  });

  const secretMut = useMutation({
    mutationFn: (secret: string) => saveSecret({ secret }),
    onSuccess: () => {
      setTurnstileSecretValue("");
      setProbes((p) => ({ ...p, turnstile: undefined }));
      void refreshStatus();
      toast.success("Turnstile secret saved");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Could not save the secret"),
  });

  const probeMut = useMutation({
    mutationFn: (kind: IntegrationTestKind) => runProbe({ kind }),
    onSuccess: (r, kind) => {
      setProbes((p) => ({ ...p, [kind]: r }));
      void refreshStatus();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "The test could not run"),
  });
  const probing = probeMut.isPending ? probeMut.variables : null;

  if (query.isLoading || !draft || !saved || !values) {
    if (query.isError) {
      return (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Settings could not be loaded</AlertTitle>
          <AlertDescription>
            {query.error instanceof Error ? query.error.message : "Unknown error"}
          </AlertDescription>
        </Alert>
      );
    }
    return <p className="p-2 text-sm text-muted-foreground">Loading settings...</p>;
  }

  const errFor = (group: SettingsGroup, path: string) => errors[group]?.[path];

  const turnstileWarning = (() => {
    const forms = draft.integrations.turnstileEnabledForms;
    if (!forms.contact && !forms.newsletter) return null;
    const missing: string[] = [];
    if (!draft.integrations.turnstileSiteKey.trim()) missing.push("site key");
    if (status && !status.turnstile.secretConfigured) missing.push("secret key");
    if (missing.length === 0) return null;
    return `Protection is switched on but the ${missing.join(" and ")} ${
      missing.length > 1 ? "are" : "is"
    } missing. Until both are in place these forms accept submissions without verification.`;
  })();

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Site Settings</CardTitle>
          <CardDescription>
            Organization details, who gets notified, how outbound email is sent, the site-wide SEO
            defaults, third-party integrations and analytics. Each section saves on its own; the
            public site picks up changes within a few minutes.
          </CardDescription>
        </CardHeader>
      </Card>

      {readOnly && (
        <Alert>
          <Eye className="h-4 w-4" />
          <AlertTitle>Read-only</AlertTitle>
          <AlertDescription>
            You can view these settings, but only administrators can change them. Ask an
            administrator if something here needs updating.
          </AlertDescription>
        </Alert>
      )}

      {/* ---------------------------------------------- Organization */}
      <GroupCard
        group="org"
        dirty={dirty.org}
        saving={saving === "org"}
        savedAt={saved.updatedAt.org}
        errors={errors.org}
        readOnly={readOnly}
        onSave={() => saveGroup("org")}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="org-companyName"
            label="Company name"
            value={draft.org.companyName}
            error={errFor("org", "companyName")}
            disabled={readOnly}
            onChange={(v) => patch("org", { companyName: v })}
            hint="Shown as the first line of the postal address on the site."
          />
          <TextField
            id="org-legalName"
            label="Legal name"
            value={draft.org.legalName}
            error={errFor("org", "legalName")}
            disabled={readOnly}
            onChange={(v) => patch("org", { legalName: v })}
            hint="Used in the footer copyright line."
          />
          <TextareaField
            id="org-addressLines"
            label="Postal address (optional)"
            value={draft.org.addressLines}
            rows={3}
            error={errFor("org", "addressLines")}
            disabled={readOnly}
            onChange={(v) => patch("org", { addressLines: v })}
            hint="One line per row. Leave empty to hide the address from the footer."
            className="sm:col-span-2"
          />
          <TextField
            id="org-phone"
            label="Phone (optional)"
            type="tel"
            value={draft.org.phone}
            error={errFor("org", "phone")}
            disabled={readOnly}
            onChange={(v) => patch("org", { phone: v })}
            className="sm:max-w-xs"
          />
          <TextField
            id="org-generalEmail"
            label="General email"
            type="email"
            value={draft.org.generalEmail}
            error={errFor("org", "generalEmail")}
            disabled={readOnly}
            onChange={(v) => patch("org", { generalEmail: v })}
            hint="Footer contact and the newsletter Subscribe link."
          />
          <TextField
            id="org-researchEmail"
            label="Research inquiries email"
            type="email"
            value={draft.org.researchEmail}
            error={errFor("org", "researchEmail")}
            disabled={readOnly}
            onChange={(v) => patch("org", { researchEmail: v })}
            hint="The Strategic Research page's contact buttons."
          />
          <TextField
            id="org-sponsorshipEmail"
            label="Sponsorship email"
            type="email"
            value={draft.org.sponsorshipEmail}
            error={errFor("org", "sponsorshipEmail")}
            disabled={readOnly}
            onChange={(v) => patch("org", { sponsorshipEmail: v })}
            hint='The Sponsors page "Become a sponsor" button.'
          />
          <TextField
            id="org-incubatorEmail"
            label="Ax Incubator email"
            type="email"
            value={draft.org.incubatorEmail}
            error={errFor("org", "incubatorEmail")}
            disabled={readOnly}
            onChange={(v) => patch("org", { incubatorEmail: v })}
          />
        </div>
      </GroupCard>

      {/* ---------------------------------------------- Notifications */}
      <GroupCard
        group="notifications"
        dirty={dirty.notifications}
        saving={saving === "notifications"}
        savedAt={saved.updatedAt.notifications}
        errors={errors.notifications}
        readOnly={readOnly}
        onSave={() => saveGroup("notifications")}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <TextareaField
            id="n-contactRecipients"
            label="Contact form recipients"
            value={draft.notifications.contactRecipients}
            rows={3}
            error={errFor("notifications", "contactRecipients")}
            disabled={readOnly}
            onChange={(v) => patch("notifications", { contactRecipients: v })}
            hint="One email address per line."
            mono
          />
          <TextareaField
            id="n-researchRecipients"
            label="Research inquiry recipients"
            value={draft.notifications.researchRecipients}
            rows={3}
            error={errFor("notifications", "researchRecipients")}
            disabled={readOnly}
            onChange={(v) => patch("notifications", { researchRecipients: v })}
            hint="One email address per line."
            mono
          />
          <TextareaField
            id="n-incubatorRecipients"
            label="Ax Incubator application recipients"
            value={draft.notifications.incubatorRecipients}
            rows={3}
            error={errFor("notifications", "incubatorRecipients")}
            disabled={readOnly}
            onChange={(v) => patch("notifications", { incubatorRecipients: v })}
            hint="One email address per line."
            mono
          />
          <TextareaField
            id="n-newsletterSignupRecipients"
            label="Newsletter sign-up recipients"
            value={draft.notifications.newsletterSignupRecipients}
            rows={3}
            error={errFor("notifications", "newsletterSignupRecipients")}
            disabled={readOnly}
            onChange={(v) => patch("notifications", { newsletterSignupRecipients: v })}
            hint="One email address per line."
            mono
          />
          <TextareaField
            id="n-healthAlertRecipients"
            label="Health alert recipients"
            value={draft.notifications.healthAlertRecipients}
            rows={3}
            error={errFor("notifications", "healthAlertRecipients")}
            disabled={readOnly}
            onChange={(v) => patch("notifications", { healthAlertRecipients: v })}
            hint="One email address per line. Alerts come from the daily System Health run."
            mono
          />
          <div className="space-y-1.5">
            <Label>Health alert frequency</Label>
            <RadioGroup
              value={draft.notifications.healthAlertMode}
              disabled={readOnly}
              onValueChange={(v) => patch("notifications", { healthAlertMode: v as HealthAlertMode })}
              className="gap-3 pt-1"
            >
              <RadioChoice
                id="mode-always"
                value="always"
                title="Every failing run"
                explanation="An email each day the checks fail, until they pass again."
              />
              <RadioChoice
                id="mode-on_change"
                value="on_change"
                title="Only when something changes, plus an all-clear"
                explanation="One email when a check starts failing and one when it recovers."
              />
            </RadioGroup>
            {errFor("notifications", "healthAlertMode") && (
              <FieldError id="n-healthAlertMode-error">
                {errFor("notifications", "healthAlertMode")}
              </FieldError>
            )}
          </div>
        </div>

        <div className="mt-5 flex flex-col gap-2 rounded-md border bg-muted/30 p-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm">
            <p className="font-medium">Send a test email</p>
            <p className="text-xs text-muted-foreground">
              {dirty.notifications
                ? "Goes to the saved contact-form recipients. Save this section first to test the new list."
                : "Sends a sample contact-form notification to the recipients above."}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={readOnly || testMut.isPending}
            onClick={() => testMut.mutate()}
          >
            {testMut.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Send className="mr-1 h-4 w-4" aria-hidden />
            )}
            {testMut.isPending ? "Sending..." : "Send test email"}
          </Button>
        </div>
      </GroupCard>

      {/* ---------------------------------------------- Email */}
      <GroupCard
        group="email"
        dirty={dirty.email}
        saving={saving === "email"}
        savedAt={saved.updatedAt.email}
        errors={errors.email}
        readOnly={readOnly}
        onSave={() => saveGroup("email")}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="e-senderName"
            label="Sender name"
            value={draft.email.senderName}
            error={errFor("email", "senderName")}
            disabled={readOnly}
            onChange={(v) => patch("email", { senderName: v })}
            hint='The display name in the From line, e.g. "MII News".'
          />
          <div className="space-y-1.5">
            <TextField
              id="e-senderAddress"
              label="Sender address"
              type="email"
              value={draft.email.senderAddress}
              error={errFor("email", "senderAddress")}
              disabled={readOnly}
              onChange={(v) => patch("email", { senderAddress: v })}
            />
            <SenderVerification
              verified={saved.senderVerified}
              changed={values.email.senderAddress !== saved.email.senderAddress}
            />
          </div>
          <TextField
            id="e-replyTo"
            label="Reply-to address (optional)"
            type="email"
            value={draft.email.replyTo}
            error={errFor("email", "replyTo")}
            disabled={readOnly}
            onChange={(v) => patch("email", { replyTo: v })}
            hint="Where replies go when it differs from the sender address."
          />
          <TextField
            id="e-subscribeSubject"
            label="Subscribe subject line"
            value={draft.email.subscribeSubject}
            error={errFor("email", "subscribeSubject")}
            disabled={readOnly}
            onChange={(v) => patch("email", { subscribeSubject: v })}
            hint="Pre-filled subject of the newsletter's Subscribe mailto link."
          />
          <TextareaField
            id="e-newsletterFooter"
            label="Newsletter footer (optional)"
            value={draft.email.newsletterFooter}
            rows={3}
            error={errFor("email", "newsletterFooter")}
            disabled={readOnly}
            onChange={(v) => patch("email", { newsletterFooter: v })}
            hint="Plain text appended under every newsletter issue, e.g. a postal address or an unsubscribe note."
            className="sm:col-span-2"
          />
        </div>
      </GroupCard>

      {/* ---------------------------------------------- SEO */}
      <GroupCard
        group="seo"
        dirty={dirty.seo}
        saving={saving === "seo"}
        savedAt={saved.updatedAt.seo}
        errors={errors.seo}
        readOnly={readOnly}
        onSave={() => saveGroup("seo")}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="s-titleSuffix"
            label="Title suffix"
            value={draft.seo.titleSuffix}
            error={errFor("seo", "titleSuffix")}
            disabled={readOnly}
            onChange={(v) => patch("seo", { titleSuffix: v })}
            hint='Appended to page titles as "Page - Suffix"; also the site title on its own.'
          />
          <TextField
            id="s-searchConsoleTag"
            label="Google Search Console verification (optional)"
            value={draft.seo.searchConsoleTag}
            error={errFor("seo", "searchConsoleTag")}
            disabled={readOnly}
            onChange={(v) => patch("seo", { searchConsoleTag: v })}
            hint='The content value of the google-site-verification meta tag, not the whole tag.'
            mono
          />
          <TextareaField
            id="s-defaultDescription"
            label="Default description"
            value={draft.seo.defaultDescription}
            rows={3}
            error={errFor("seo", "defaultDescription")}
            disabled={readOnly}
            onChange={(v) => patch("seo", { defaultDescription: v })}
            hint="What search engines and link previews show for pages without their own description. Aim for under 160 characters."
            className="sm:col-span-2"
          />
          <div className="sm:col-span-2">
            <MediaField
              label="Default share image (optional)"
              value={draft.seo.defaultShareImage}
              target={SHARE_IMAGE_TARGET}
              disabled={readOnly}
              onChange={(url) => patch("seo", { defaultShareImage: url })}
              hint="Shown when a page is shared on LinkedIn, X or Facebook and has no image of its own. 1200 x 630 works best."
              preview="thumb"
            />
            {errFor("seo", "defaultShareImage") && (
              <FieldError id="s-defaultShareImage-error">
                {errFor("seo", "defaultShareImage")}
              </FieldError>
            )}
          </div>
        </div>

        <fieldset className="mt-5 rounded-lg border bg-card p-4">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Social profiles
          </legend>
          <p className="mb-3 text-xs text-muted-foreground">
            Full profile addresses. Only filled-in profiles appear in the site footer.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            {(
              [
                ["linkedin", "LinkedIn"],
                ["x", "X (Twitter)"],
                ["facebook", "Facebook"],
                ["instagram", "Instagram"],
                ["youtube", "YouTube"],
              ] as const
            ).map(([key, label]) => (
              <TextField
                key={key}
                id={`s-social-${key}`}
                label={`${label} (optional)`}
                type="url"
                placeholder="https://"
                value={draft.seo.social[key]}
                error={errFor("seo", `social.${key}`)}
                disabled={readOnly}
                onChange={(v) => patch("seo", { social: { ...draft.seo.social, [key]: v } })}
              />
            ))}
          </div>
        </fieldset>
      </GroupCard>

      {/* ---------------------------------------------- Integrations */}
      <GroupCard
        group="integrations"
        dirty={dirty.integrations}
        saving={saving === "integrations"}
        savedAt={saved.updatedAt.integrations}
        errors={errors.integrations}
        readOnly={readOnly}
        onSave={() => saveGroup("integrations")}
      >
        {statusQuery.isError && (
          <Alert className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Live status unavailable</AlertTitle>
            <AlertDescription>
              {statusQuery.error instanceof Error
                ? statusQuery.error.message
                : "The integration status could not be loaded."}{" "}
              Settings can still be edited and saved.
            </AlertDescription>
          </Alert>
        )}

        {/* ---- Shopify */}
        <fieldset className="rounded-lg border bg-card p-4">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Shopify
          </legend>
          <p className="mb-3 text-xs text-muted-foreground">
            The store behind the Research catalogue and checkout. The store domain and API version
            are set in the deployment; the tokens live in AWS Secrets Manager and only their status
            is shown here.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <ReadOnlyField
              label="Store domain"
              value={status ? status.shopify.storeDomain || "Not set" : "..."}
              mono
            />
            <ReadOnlyField
              label="API version"
              value={status ? status.shopify.apiVersion || "Not set" : "..."}
              mono
            />
          </div>

          <div className="mt-4 divide-y rounded-md border">
            <ProbeRow
              title="Storefront API"
              description="Read-only access that lists products and builds the cart."
              pill={storefrontPill(status, probes["shopify-storefront"], statusQuery.isError)}
              detail={probes["shopify-storefront"]?.detail ?? status?.shopify.storefront.detail}
              checkedAt={
                probes["shopify-storefront"]?.checkedAt ?? status?.shopify.storefront.checkedAt
              }
              canTest={!!status?.shopify.storeDomain}
              testing={probing === "shopify-storefront"}
              disabled={probeMut.isPending}
              onTest={() => probeMut.mutate("shopify-storefront")}
            />
            <ProbeRow
              title="Admin API"
              description="Write access used by Admin > Research to create and update products."
              pill={shopifyAdminPill(status, probes["shopify-admin"], statusQuery.isError)}
              detail={probes["shopify-admin"]?.detail ?? status?.shopify.admin.detail}
              checkedAt={probes["shopify-admin"]?.checkedAt}
              note={
                status?.shopify.admin.lastChangedAt
                  ? `Token changed ${formatSaved(status.shopify.admin.lastChangedAt) ?? ""}`
                  : undefined
              }
              canTest={!!status?.shopify.admin.configured}
              testing={probing === "shopify-admin"}
              disabled={probeMut.isPending}
              onTest={() => probeMut.mutate("shopify-admin")}
            />
          </div>

          {!readOnly && (
            <div className="mt-4">
              <SecretField
                id="i-shopifyAdminToken"
                label="Update admin token"
                value={adminToken}
                placeholder="shpat_..."
                submitLabel="Save token"
                pending={tokenMut.isPending}
                onChange={setAdminToken}
                onSubmit={submitAdminToken}
                hint="The Admin API access token of the store's custom app (Shopify admin > Settings > Apps and sales channels > Develop apps); it starts with shpat_. Stored once, checked right away and never shown again."
              />
            </div>
          )}
        </fieldset>

        {/* ---- Turnstile */}
        <fieldset className="mt-5 rounded-lg border bg-card p-4">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Form spam protection
          </legend>
          <p className="mb-3 text-xs text-muted-foreground">
            Cloudflare Turnstile adds a silent bot check to the public forms. The site key is public
            and saves with this section; the secret key is stored on its own and never shown again.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="i-turnstileSiteKey"
              label="Turnstile site key (optional)"
              value={draft.integrations.turnstileSiteKey}
              error={errFor("integrations", "turnstileSiteKey")}
              disabled={readOnly}
              onChange={(v) => patch("integrations", { turnstileSiteKey: v })}
              hint="From the Cloudflare dashboard: Turnstile > your widget > Site key."
              mono
            />
            <div className="space-y-1.5">
              {readOnly ? (
                <ReadOnlyField label="Secret key" value="Write-only" />
              ) : (
                <SecretField
                  id="i-turnstileSecret"
                  label="Secret key"
                  value={turnstileSecret}
                  placeholder="0x..."
                  submitLabel="Save secret"
                  pending={secretMut.isPending}
                  onChange={setTurnstileSecretValue}
                  onSubmit={submitTurnstileSecret}
                />
              )}
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                <span className="inline-flex flex-wrap items-center gap-2">
                  <StatusPill pill={turnstilePill(status, probes.turnstile, statusQuery.isError)} />
                  <span>
                    {status
                      ? status.turnstile.secretConfigured
                        ? `Secret configured${
                            status.turnstile.lastChangedAt
                              ? `, changed ${formatSaved(status.turnstile.lastChangedAt) ?? ""}`
                              : ""
                          }`
                        : "No secret yet"
                      : statusQuery.isError
                        ? "Status unavailable"
                        : "Checking..."}
                  </span>
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!status?.turnstile.secretConfigured || probeMut.isPending}
                  onClick={() => probeMut.mutate("turnstile")}
                >
                  {probing === "turnstile" ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <RefreshCw className="mr-1 h-4 w-4" aria-hidden />
                  )}
                  {probing === "turnstile" ? "Testing..." : "Test"}
                </Button>
              </div>
              {probes.turnstile?.detail && (
                <p className="text-xs text-muted-foreground">{probes.turnstile.detail}</p>
              )}
            </div>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <SwitchField
              id="i-protect-contact"
              label="Protect the contact form"
              description="Contact, Ax Incubator and custom-research inquiries."
              checked={draft.integrations.turnstileEnabledForms.contact}
              disabled={readOnly}
              onCheckedChange={(v) =>
                patch("integrations", {
                  turnstileEnabledForms: {
                    ...draft.integrations.turnstileEnabledForms,
                    contact: v,
                  },
                })
              }
            />
            <SwitchField
              id="i-protect-newsletter"
              label="Protect newsletter sign-up"
              description="The Aesthetic Insights sign-up on the home page."
              checked={draft.integrations.turnstileEnabledForms.newsletter}
              disabled={readOnly}
              onCheckedChange={(v) =>
                patch("integrations", {
                  turnstileEnabledForms: {
                    ...draft.integrations.turnstileEnabledForms,
                    newsletter: v,
                  },
                })
              }
            />
          </div>
          {turnstileWarning && (
            <p className="mt-3 flex items-start gap-1.5 text-xs text-amber-800">
              <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>{turnstileWarning}</span>
            </p>
          )}
        </fieldset>
      </GroupCard>

      {/* ---------------------------------------------- Analytics */}
      <GroupCard
        group="analytics"
        dirty={dirty.analytics}
        saving={saving === "analytics"}
        savedAt={saved.updatedAt.analytics}
        errors={errors.analytics}
        readOnly={readOnly}
        onSave={() => saveGroup("analytics")}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="a-ga4MeasurementId"
            label="GA4 measurement ID (optional)"
            placeholder="G-XXXXXXXXXX"
            value={draft.analytics.ga4MeasurementId}
            error={errFor("analytics", "ga4MeasurementId")}
            disabled={readOnly}
            onChange={(v) => patch("analytics", { ga4MeasurementId: v })}
            hint="Google Analytics > Admin > Data streams > your web stream. Leave empty to turn Google Analytics off."
            mono
          />
          <TextField
            id="a-plausibleDomain"
            label="Plausible domain (optional)"
            placeholder="www.miinews.com"
            value={draft.analytics.plausibleDomain}
            error={errFor("analytics", "plausibleDomain")}
            disabled={readOnly}
            onChange={(v) => patch("analytics", { plausibleDomain: v })}
            hint="The site name as added in Plausible; loads its script alongside GA4."
            mono
          />
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <SwitchField
            id="a-consentBannerEnabled"
            label="Show a consent banner before loading analytics"
            description="Nothing loads until the visitor accepts; a decline is remembered for 180 days."
            checked={draft.analytics.consentBannerEnabled}
            disabled={readOnly}
            onCheckedChange={(v) => patch("analytics", { consentBannerEnabled: v })}
          />
          <SwitchField
            id="a-excludeAdmin"
            label="Do not track admin pages"
            description="Skips /admin and /auth so staff visits do not count."
            checked={draft.analytics.excludeAdmin}
            disabled={readOnly}
            onCheckedChange={(v) => patch("analytics", { excludeAdmin: v })}
          />
        </div>

        {draft.analytics.consentBannerEnabled && (
          <div className="mt-4">
            <TextareaField
              id="a-consentText"
              label="Consent banner text"
              value={draft.analytics.consentText}
              rows={2}
              error={errFor("analytics", "consentText")}
              disabled={readOnly}
              onChange={(v) => patch("analytics", { consentText: v })}
              hint='Shown in a compact bar at the bottom of the page with "Accept" and "Decline" buttons.'
            />
          </div>
        )}

        <div className="mt-5 flex flex-col gap-2 rounded-md border bg-muted/30 p-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm">
            <p className="font-medium">Live status</p>
            <p className="text-xs text-muted-foreground">
              {status
                ? `Google Analytics ${status.analytics.ga4 ? "on" : "off"}, Plausible ${
                    status.analytics.plausible ? "on" : "off"
                  }.${dirty.analytics ? " Reflects the saved values; save this section to apply the edits above." : ""}`
                : statusQuery.isError
                  ? "Status unavailable."
                  : "Checking..."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <StatusPill
              pill={
                !status
                  ? statusQuery.isError
                    ? UNAVAILABLE_PILL
                    : PENDING_PILL
                  : status.analytics.ga4 || status.analytics.plausible
                    ? { tone: "ok", label: "Tracking" }
                    : { tone: "neutral", label: "Off" }
              }
            />
          </div>
        </div>
      </GroupCard>

      {/* ---------------------------------------------- sticky footer */}
      {!readOnly && (
        <div className="sticky bottom-0 z-30 -mx-4 -mb-4 border-t bg-background/95 backdrop-blur sm:-mx-6 sm:-mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {anyDirty
                ? `Unsaved changes in ${dirtyGroups.map((g) => GROUP_META[g].title).join(", ")}`
                : "All changes saved"}
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={!anyDirty || saving !== null}
                onClick={discardAll}
              >
                Discard changes
              </Button>
              <Button
                type="button"
                disabled={!anyDirty || saving !== null}
                title={!anyDirty ? "No changes to save" : undefined}
                onClick={() => void saveAll()}
              >
                {saving !== null ? (
                  <>
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden /> Saving...
                  </>
                ) : dirtyGroups.length > 1 ? (
                  "Save all changes"
                ) : (
                  "Save changes"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog
        open={blocker.status === "blocked"}
        onOpenChange={(open) => {
          if (!open) blocker.reset?.();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes in{" "}
              {dirtyGroups.map((g) => GROUP_META[g].title).join(", ") || "this page"}. If you
              leave now they will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => blocker.reset?.()}>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={() => blocker.proceed?.()}>Discard changes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ----------------------------------------------------------- components */

/**
 * One labeled category card per settings group: tinted header (outline icon,
 * title, one-line context), the fields, and a footer with the last save time
 * and a Save button that stays disabled until the group is dirty.
 */
function GroupCard({
  group,
  dirty,
  saving,
  savedAt,
  errors,
  readOnly,
  onSave,
  children,
}: {
  group: SettingsGroup;
  dirty: boolean;
  saving: boolean;
  savedAt: string | undefined;
  errors: FieldErrors | undefined;
  readOnly: boolean;
  onSave: () => void;
  children: ReactNode;
}) {
  const meta = GROUP_META[group];
  const Icon = meta.icon;
  const errorList = errors ? Object.entries(errors) : [];
  const savedLabel = formatSaved(savedAt);

  return (
    <Card
      id={`settings-${group}`}
      className={cn("overflow-hidden", dirty && "ring-1 ring-primary/40")}
    >
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (!readOnly && dirty && !saving) onSave();
        }}
      >
        <CardHeader className="flex flex-row items-start gap-3 space-y-0 border-b bg-secondary/60 px-6 py-4">
          <Icon className="mt-0.5 h-5 w-5 shrink-0 text-slate-600" aria-hidden />
          <div className="min-w-0">
            <CardTitle className="text-base">{meta.title}</CardTitle>
            <CardDescription className="mt-0.5">{meta.description}</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="px-6 py-5">
          {errorList.length > 0 && (
            <Alert variant="destructive" className="mb-4" role="alert" tabIndex={-1}>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>There is a problem</AlertTitle>
              <AlertDescription>
                <ul className="mt-1 list-disc space-y-0.5 pl-4">
                  {errorList.map(([path, msg]) => (
                    <li key={path}>
                      <span className="font-medium">{humanizePath(path)}:</span> {msg}
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}
          {children}
        </CardContent>
        {!readOnly && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t bg-muted/20 px-6 py-3">
            <span className="text-xs text-muted-foreground">
              {dirty ? "Unsaved changes" : savedLabel ? `Saved ${savedLabel}` : "Using defaults"}
            </span>
            <Button
              type="submit"
              size="sm"
              disabled={!dirty || saving}
              title={!dirty && !saving ? "No changes to save" : undefined}
            >
              {saving ? (
                <>
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden /> Saving...
                </>
              ) : (
                meta.saveLabel
              )}
            </Button>
          </div>
        )}
      </form>
    </Card>
  );
}

function humanizePath(path: string): string {
  return path
    .replace(/\./g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^\w/, (c) => c.toUpperCase());
}

function FieldError({ id, children }: { id: string; children: ReactNode }) {
  return (
    <p id={id} className="text-xs text-destructive">
      {children}
    </p>
  );
}

function FieldHint({ id, children }: { id: string; children: ReactNode }) {
  return (
    <p id={id} className="text-xs text-muted-foreground">
      {children}
    </p>
  );
}

function TextField({
  id,
  label,
  value,
  onChange,
  error,
  hint,
  disabled,
  type = "text",
  placeholder,
  mono,
  className,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  hint?: string;
  disabled?: boolean;
  type?: "text" | "email" | "tel" | "url";
  placeholder?: string;
  mono?: boolean;
  className?: string;
}) {
  const errId = `${id}-error`;
  const hintId = `${id}-hint`;
  return (
    <div className={cn("space-y-1.5", className)}>
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={type}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={[error ? errId : null, hint ? hintId : null].filter(Boolean).join(" ") || undefined}
        onChange={(e) => onChange(e.target.value)}
        className={cn(mono && "font-mono text-sm", error && "border-destructive")}
      />
      {error && <FieldError id={errId}>{error}</FieldError>}
      {hint && <FieldHint id={hintId}>{hint}</FieldHint>}
    </div>
  );
}

function TextareaField({
  id,
  label,
  value,
  onChange,
  error,
  hint,
  disabled,
  rows = 3,
  mono,
  className,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  hint?: string;
  disabled?: boolean;
  rows?: number;
  mono?: boolean;
  className?: string;
}) {
  const errId = `${id}-error`;
  const hintId = `${id}-hint`;
  return (
    <div className={cn("space-y-1.5", className)}>
      <Label htmlFor={id}>{label}</Label>
      <Textarea
        id={id}
        value={value}
        rows={rows}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={[error ? errId : null, hint ? hintId : null].filter(Boolean).join(" ") || undefined}
        onChange={(e) => onChange(e.target.value)}
        className={cn(mono && "font-mono text-sm", error && "border-destructive")}
      />
      {error && <FieldError id={errId}>{error}</FieldError>}
      {hint && <FieldHint id={hintId}>{hint}</FieldHint>}
    </div>
  );
}

function RadioChoice({
  id,
  value,
  title,
  explanation,
}: {
  id: string;
  value: HealthAlertMode;
  title: string;
  explanation: string;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <RadioGroupItem id={id} value={value} className="mt-0.5" />
      <Label htmlFor={id} className="cursor-pointer font-normal leading-snug">
        <span className="block font-medium">{title}</span>
        <span className="block text-xs text-muted-foreground">{explanation}</span>
      </Label>
    </div>
  );
}

function StatusPill({ pill }: { pill: Pill }) {
  const style = PILL_STYLE[pill.tone];
  const Icon = style.icon;
  return (
    <Badge className={cn("gap-1 rounded-full font-medium", style.className)}>
      <Icon className="h-3 w-3" aria-hidden />
      {pill.label}
    </Badge>
  );
}

/** Value set in the deployment, shown like a field so the group reads as one form. */
function ReadOnlyField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div
        className={cn(
          "flex h-9 items-center rounded-md border bg-muted/40 px-3 text-sm text-muted-foreground",
          mono && "font-mono",
        )}
      >
        {value}
      </div>
    </div>
  );
}

/** One connectivity check: title, status pill, last detail and a Test button. */
function ProbeRow({
  title,
  description,
  pill,
  detail,
  checkedAt,
  note,
  canTest,
  testing,
  disabled,
  onTest,
}: {
  title: string;
  description: string;
  pill: Pill;
  detail?: string;
  checkedAt?: string;
  note?: string;
  canTest: boolean;
  testing: boolean;
  disabled: boolean;
  onTest: () => void;
}) {
  const checkedLabel = checkedAt ? formatSaved(checkedAt) : null;
  return (
    <div className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{title}</span>
          <StatusPill pill={pill} />
        </div>
        <p className="text-xs text-muted-foreground">{description}</p>
        {detail && (
          <p className="mt-0.5 text-xs text-muted-foreground">
            {detail}
            {checkedLabel ? ` (checked ${checkedLabel})` : ""}
          </p>
        )}
        {note && <p className="mt-0.5 text-xs text-muted-foreground">{note}</p>}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="shrink-0"
        disabled={!canTest || disabled}
        onClick={onTest}
      >
        {testing ? (
          <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden />
        ) : (
          <RefreshCw className="mr-1 h-4 w-4" aria-hidden />
        )}
        {testing ? "Testing..." : "Test"}
      </Button>
    </div>
  );
}

/**
 * Write-only secret: a password input with its own submit. Never prefilled,
 * cleared after a successful save, and outside the group draft so it takes no
 * part in dirty tracking. Enter submits the secret, not the surrounding group.
 */
function SecretField({
  id,
  label,
  value,
  onChange,
  onSubmit,
  submitLabel,
  pending,
  placeholder,
  hint,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  submitLabel: string;
  pending: boolean;
  placeholder?: string;
  hint?: string;
}) {
  const hintId = `${id}-hint`;
  const canSubmit = value.trim().length > 0 && !pending;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex gap-2">
        <Input
          id={id}
          type="password"
          autoComplete="new-password"
          spellCheck={false}
          value={value}
          placeholder={placeholder}
          disabled={pending}
          aria-describedby={hint ? hintId : undefined}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (canSubmit) onSubmit();
            }
          }}
          className="font-mono text-sm"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 shrink-0"
          disabled={!canSubmit}
          onClick={onSubmit}
        >
          {pending ? (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <KeyRound className="mr-1 h-4 w-4" aria-hidden />
          )}
          {pending ? "Saving..." : submitLabel}
        </Button>
      </div>
      {hint && <FieldHint id={hintId}>{hint}</FieldHint>}
    </div>
  );
}

function SwitchField({
  id,
  label,
  description,
  checked,
  disabled,
  onCheckedChange,
}: {
  id: string;
  label: string;
  description?: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (v: boolean) => void;
}) {
  const hintId = `${id}-hint`;
  return (
    <div className="flex items-start justify-between gap-4 rounded-md border bg-muted/20 px-3 py-2.5">
      <div className="min-w-0 space-y-0.5">
        <Label htmlFor={id} className="cursor-pointer leading-snug">
          {label}
        </Label>
        {description && <FieldHint id={hintId}>{description}</FieldHint>}
      </div>
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        aria-describedby={description ? hintId : undefined}
        onCheckedChange={onCheckedChange}
        className="mt-0.5"
      />
    </div>
  );
}

function SenderVerification({ verified, changed }: { verified: boolean; changed: boolean }) {
  if (changed) {
    return (
      <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
        <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>Verification is checked against the saved address; save to see the status of this one.</span>
      </p>
    );
  }
  if (verified) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-emerald-700">
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
        Verified with Amazon SES
      </p>
    );
  }
  return (
    <div className="space-y-0.5 text-xs">
      <p className="flex items-start gap-1.5 text-amber-800">
        <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>Not verified: emails from this address will fail until it is verified.</span>
      </p>
      <p className="pl-5 text-muted-foreground">
        Verifying means adding the DNS records Amazon SES issues for this address's domain.
      </p>
    </div>
  );
}
